import type React from 'react';
import { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router';

import useAppNavigation from '@/hooks/useAppNavigation';
import { isNil } from 'lodash-es';
import {
  selectBestBusiness,
  selectBestService,
  sortAvailableSlots,
} from '../../../common/logic';
import useCart from '../../../hooks/useCart';
import type { PartnerBusinessDescriptor } from '../../../proxies/SearchProxy';
import type { CartSelectionType } from '../../../redux/ducks/CartDucks.types';
import {
  setCurrentPartnerBusiness,
  setCurrentPartnerBusinessService,
} from '../../../redux/ducks/PartnerSelectionDucks';
import {
  getPartnerAvailabilityRequest,
  getPartnerAvailabilityResponse as getPartnerAvailabilityResponseCall,
  getPartnerDetailsByPathRequest,
  getPartnerDetailsRequest,
  getPartnerDetailsResponse as getPartnerDetailsResponseCall,
} from '../../../redux/ducks/SearchDucks';
import type {
  GetPartnerAvailabilityRequestType,
  GetPartnerAvailabilityResponseDataType,
  PartnerDetailsDescriptor,
} from '../../../redux/ducks/SearchDucks.types';
import type { RootReducerType } from '../../../redux/reducers/RootReducer.types';
import type { AppSsrFetchedDataType } from '../../../server/AppSsr';
import MainLayout from '../../layout/MainLayout/MainLayout';
import Spinner from '../../layout/Spinner';
import PartnerSeoPage from './PartnerSeoPage';

interface PartnerProfileProps {
  ssrFetchedData: AppSsrFetchedDataType;
  byPath: boolean;
}

const PartnerProfile: React.FC<PartnerProfileProps> = ({
  ssrFetchedData,
  byPath,
}) => {
  const dispatch = useDispatch();
  const { isSameRoute } = useAppNavigation();
  const { partnerId, partnerPath } = useParams();
  const { currentCart, resetCart, setCart } = useCart();
  const [onClient, setOnClient] = useState<boolean>(false);

  const { getPartnerDetailsResponse, isGettingPartnerAvailability } =
    useSelector((state: RootReducerType) => state.search);
  const currentPartnerBusiness = useSelector(
    (state: RootReducerType) => state.partnerSelection.currentPartnerBusiness,
  );
  const currentPartnerBusinessService = useSelector(
    (state: RootReducerType) =>
      state.partnerSelection.currentPartnerBusinessService,
  );

  const getPartnerAvailabilityResponse = useSelector(
    (state: RootReducerType) => state.search.getPartnerAvailabilityResponse,
  );

  const validSsrFetchedData = useMemo<AppSsrFetchedDataType | undefined>(() => {
    const valid =
      (import.meta.env.SSR && !isNil(ssrFetchedData?.partnerDetails)) ||
      (!import.meta.env.SSR &&
        !isNil(ssrFetchedData?.partnerDetails) &&
        ((byPath &&
          !isNil(partnerPath) &&
          isSameRoute(ssrFetchedData.partnerDetails.path, partnerPath)) ||
          (!byPath && ssrFetchedData.partnerDetails.id === partnerId)));

    return valid ? ssrFetchedData : undefined;
  }, [ssrFetchedData, partnerPath, partnerId, byPath]);

  const validPartnerDetailsData = useMemo<
    PartnerDetailsDescriptor | undefined
  >(() => {
    const valid =
      !isNil(getPartnerDetailsResponse?.data) &&
      ((byPath && getPartnerDetailsResponse.data.path === partnerPath) ||
        (!byPath && getPartnerDetailsResponse.data.id === partnerId));

    return valid && !isNil(getPartnerDetailsResponse?.data)
      ? getPartnerDetailsResponse.data
      : undefined;
  }, [getPartnerDetailsResponse?.data, partnerPath, partnerId, byPath]);

  const partnerProfile = useMemo<PartnerDetailsDescriptor | undefined>(() => {
    if (!isNil(validPartnerDetailsData)) {
      return validPartnerDetailsData;
    }

    if (validSsrFetchedData) {
      return validSsrFetchedData.partnerDetails;
    }

    return undefined;
  }, [validPartnerDetailsData, validSsrFetchedData]);

  const selectedBusiness = useMemo<string | undefined>(() => {
    if (partnerProfile) {
      if (
        !isNil(currentPartnerBusiness) &&
        partnerProfile.businesses.filter(
          (b) => b.id === currentPartnerBusiness.id && b.active,
        ).length > 0
      ) {
        return currentPartnerBusiness.id;
      }

      let selected: PartnerBusinessDescriptor | undefined = undefined;
      if (partnerProfile) {
        selected = selectBestBusiness(partnerProfile);
      }

      return selected?.id;
    }

    return undefined;
  }, [partnerProfile, currentPartnerBusiness]);

  const selectedService = useMemo<string | undefined>(() => {
    if (!isNil(partnerProfile)) {
      if (!isNil(selectedBusiness)) {
        const matchingBusinesses = partnerProfile.businesses.filter(
          (b) => b.id === selectedBusiness,
        );

        if (matchingBusinesses.length > 0) {
          let service = undefined;
          if (!isNil(currentPartnerBusinessService)) {
            const matchingServices = matchingBusinesses[0].services.filter(
              (s) => s === currentPartnerBusinessService.id,
            );
            if (matchingServices.length > 0) {
              service = matchingServices[0];
            }
          }

          if (!isNil(service)) {
            return service;
          }

          const selected = selectBestService(
            partnerProfile,
            matchingBusinesses[0],
          );
          if (!isNil(selected)) {
            return selected.id;
          }
        }
      }
    }

    return undefined;
  }, [partnerProfile, selectedBusiness, currentPartnerBusinessService]);

  const validPartnerAvailabilityData = useMemo<
    GetPartnerAvailabilityResponseDataType | undefined
  >(() => {
    const valid =
      !isNil(partnerProfile) &&
      !isNil(selectedBusiness) &&
      !isNil(selectedService) &&
      !isNil(getPartnerAvailabilityResponse?.data) &&
      getPartnerAvailabilityResponse.data.partner === partnerProfile.id &&
      (getPartnerAvailabilityResponse.data.intervals.length === 0 ||
        (getPartnerAvailabilityResponse.data.intervals[0].service ===
          selectedService &&
          getPartnerAvailabilityResponse.data.intervals[0].business ===
            selectedBusiness));

    return valid && !isNil(getPartnerAvailabilityResponse?.data)
      ? getPartnerAvailabilityResponse.data
      : undefined;
  }, [
    getPartnerAvailabilityResponse?.data,
    partnerProfile,
    selectedBusiness,
    selectedService,
  ]);

  const partnerAvailability = useMemo<
    GetPartnerAvailabilityResponseDataType | undefined
  >(() => {
    if (!isNil(validPartnerAvailabilityData)) {
      return validPartnerAvailabilityData;
    }

    if (!isNil(validSsrFetchedData?.partnerAvailability)) {
      const valid =
        !isNil(partnerProfile) &&
        !isNil(selectedBusiness) &&
        !isNil(selectedService) &&
        validSsrFetchedData.partnerAvailability.partner === partnerProfile.id &&
        (validSsrFetchedData.partnerAvailability.intervals.length === 0 ||
          (validSsrFetchedData.partnerAvailability.intervals[0].service ===
            selectedService &&
            validSsrFetchedData.partnerAvailability.intervals[0].business ===
              selectedBusiness));

      return valid ? validSsrFetchedData.partnerAvailability : undefined;
    }

    return undefined;
  }, [
    validPartnerAvailabilityData,
    partnerProfile,
    validSsrFetchedData,
    selectedBusiness,
    selectedService,
  ]);

  const selectedInterval = useMemo<number | undefined>(() => {
    if (!isNil(currentCart)) {
      return currentCart.from;
    }

    if (
      !isNil(partnerAvailability) &&
      partnerAvailability.intervals.length > 0
    ) {
      const sortedSlots = sortAvailableSlots(
        partnerAvailability.intervals,
        undefined,
      );

      return sortedSlots[0].start;
    }

    if (validSsrFetchedData) {
      if (
        !isNil(validSsrFetchedData?.partnerSelectedBusiness) &&
        !isNil(validSsrFetchedData?.partnerSelectedService) &&
        !isNil(validSsrFetchedData?.partnerSelectedInterval) &&
        validSsrFetchedData.partnerSelectedBusiness === selectedBusiness &&
        validSsrFetchedData.partnerSelectedService === selectedService
      ) {
        return validSsrFetchedData.partnerSelectedInterval;
      }
    }

    return undefined;
  }, [
    currentCart,
    partnerAvailability,
    validSsrFetchedData,
    selectedBusiness,
    selectedService,
  ]);

  const selectedCart = useMemo<CartSelectionType | undefined>(() => {
    if (onClient && !isNil(currentCart)) {
      return currentCart;
    }

    if (!isNil(partnerProfile) && !isNil(partnerAvailability)) {
      if (
        !isNil(selectedInterval) &&
        !isNil(selectedBusiness) &&
        !isNil(selectedService)
      ) {
        const targetBiz = partnerProfile?.businesses.filter(
          (b) => b.active && b.id === selectedBusiness,
        );
        const targetSrv = partnerProfile?.services.filter(
          (s) => s.active && s.id === selectedService,
        );
        const targetInterval = partnerAvailability?.intervals.filter(
          (interval) => interval.start === selectedInterval,
        );

        if (
          targetBiz.length > 0 &&
          targetSrv.length > 0 &&
          targetInterval.length > 0
        ) {
          const cart: CartSelectionType = {
            partner: {
              id: partnerProfile.id,
              fullName: `${partnerProfile.first_name} ${partnerProfile.last_name}`,
            },
            business: {
              id: targetBiz[0].id,
              name: targetBiz[0].name,
              fullAddress: `${targetBiz[0].location.address}, ${targetBiz[0].location.city}`,
              latitude: targetBiz[0].location.latitude,
              longitude: targetBiz[0].location.longitude,
            },
            service: {
              id: targetSrv[0].id,
              name: targetSrv[0].name,
              price: targetSrv[0].price,
              currency: targetSrv[0].currency,
              duration: targetSrv[0].duration,
            },
            from: targetInterval[0].start,
            to: targetInterval[0].end,
          };

          return cart;
        }
      }
    }

    return undefined;
  }, [
    currentCart,
    selectedBusiness,
    selectedService,
    selectedInterval,
    partnerProfile,
    partnerAvailability,
    onClient,
  ]);

  useEffect(() => {
    if (!isNil(partnerProfile)) {
      if (!isNil(selectedBusiness)) {
        const targetBiz = partnerProfile.businesses.filter(
          (b) => b.id === selectedBusiness,
        );

        if (targetBiz.length > 0) {
          if (isNil(currentPartnerBusiness)) {
            dispatch(setCurrentPartnerBusiness(targetBiz[0]));
          }
        }
      } else if (!isNil(currentPartnerBusiness)) {
        dispatch(setCurrentPartnerBusiness(null));
      }
    }
  }, [selectedBusiness, currentPartnerBusiness, partnerProfile, dispatch]);

  useEffect(() => {
    if (!isNil(partnerProfile)) {
      if (!isNil(selectedService)) {
        const targetSrv = partnerProfile.services.filter(
          (s) => s.id === selectedService,
        );

        if (targetSrv.length > 0) {
          if (isNil(currentPartnerBusinessService)) {
            dispatch(setCurrentPartnerBusinessService(targetSrv[0]));
          }
        }
      } else if (!isNil(currentPartnerBusinessService)) {
        dispatch(setCurrentPartnerBusinessService(null));
      }
    }
  }, [
    selectedService,
    currentPartnerBusinessService,
    partnerProfile,
    dispatch,
  ]);

  useEffect(() => {
    if (isNil(partnerProfile)) {
      dispatch(setCurrentPartnerBusiness(null));
      dispatch(setCurrentPartnerBusinessService(null));
      resetCart();

      if (byPath && !isNil(partnerPath)) {
        dispatch(
          getPartnerDetailsByPathRequest({
            partnerPath: partnerPath,
          }),
        );
      } else if (byPath && !isNil(partnerId)) {
        dispatch(
          getPartnerDetailsRequest({
            partnerId: partnerId,
          }),
        );
      }
    }
  }, [partnerProfile, byPath, dispatch, partnerId, partnerPath, resetCart]);

  useEffect(() => {
    if (
      !isNil(partnerProfile) &&
      !isNil(selectedBusiness) &&
      !isNil(selectedService)
    ) {
      const shouldTriggerAvailabilityCall =
        isNil(partnerAvailability) && !isGettingPartnerAvailability;

      if (shouldTriggerAvailabilityCall) {
        const request: GetPartnerAvailabilityRequestType = {
          partnerId: partnerProfile.id,
          serviceId: selectedService,
          businessId: selectedBusiness,
          fromTime: !isNil(getPartnerAvailabilityResponse?.data)
            ? getPartnerAvailabilityResponse.data?.from
            : undefined,
          toTime: !isNil(getPartnerAvailabilityResponse?.data)
            ? getPartnerAvailabilityResponse.data?.to
            : undefined,
        };

        dispatch(getPartnerAvailabilityRequest(request));
      }
    }
  }, [
    partnerProfile,
    partnerAvailability,
    selectedBusiness,
    selectedService,
    dispatch,
    getPartnerAvailabilityResponse,
    isGettingPartnerAvailability,
  ]);

  useEffect(() => {
    if (isNil(partnerProfile)) {
      if (byPath && !isNil(partnerPath)) {
        dispatch(
          getPartnerDetailsByPathRequest({
            partnerPath: partnerPath,
          }),
        );
      } else if (!isNil(partnerId)) {
        dispatch(
          getPartnerDetailsRequest({
            partnerId: partnerId,
          }),
        );
      }
    }
  }, [partnerProfile, byPath, partnerId, partnerPath, dispatch]);

  useEffect(() => {
    if (
      !isNil(partnerProfile) &&
      !isNil(partnerAvailability) &&
      !isNil(selectedBusiness) &&
      !isNil(selectedService)
    ) {
      const targetBusiness = partnerProfile.businesses.filter(
        (b) => b.id === selectedBusiness,
      );
      const targetService = partnerProfile.services.filter(
        (s) => s.id === selectedService,
      );

      /*
      console.log(
        'Selection has changed',
        targetBusiness,
        selectedBusiness,
        currentPartnerBusiness,
        targetService,
        selectedService,
        currentPartnerBusinessService
      );
      */

      if (!isNil(targetBusiness) && !isNil(targetService)) {
        // find first available in partnerAvailability
        if (partnerAvailability.intervals.length > 0) {
          // First check if the interval is already available (cached cart)
          const sortedSlots = sortAvailableSlots(
            partnerAvailability.intervals,
            selectedInterval,
          );

          const cart: CartSelectionType = {
            partner: {
              id: partnerProfile.id,
              fullName: `${partnerProfile.first_name} ${partnerProfile.last_name}`,
            },
            business: {
              id: targetBusiness[0].id,
              name: targetBusiness[0].name,
              fullAddress: `${targetBusiness[0].location.address}, ${targetBusiness[0].location.city}`,
              latitude: targetBusiness[0].location.latitude,
              longitude: targetBusiness[0].location.longitude,
            },
            service: {
              id: targetService[0].id,
              name: targetService[0].name,
              price: targetService[0].price,
              currency: targetService[0].currency,
              duration: targetService[0].duration,
            },
            from: sortedSlots[0].start,
            to: sortedSlots[0].end,
          };

          setCart(cart);
        }
      } else {
        resetCart();
      }
    }
  }, [
    selectedBusiness,
    selectedService,
    partnerProfile,
    partnerAvailability,
    resetCart,
    setCart,
    selectedInterval,
  ]);

  useEffect(() => {
    if (!isNil(validSsrFetchedData?.partnerDetails)) {
      dispatch(
        getPartnerDetailsResponseCall({
          data: validSsrFetchedData.partnerDetails,
          error: null,
        }),
      );
    }
    if (!isNil(validSsrFetchedData?.partnerAvailability)) {
      dispatch(
        getPartnerAvailabilityResponseCall({
          data: validSsrFetchedData.partnerAvailability,
          error: null,
        }),
      );
    }
  }, [validSsrFetchedData?.partnerDetails, dispatch, validSsrFetchedData]);

  useEffect(() => {
    setOnClient(true);
  });

  return !partnerProfile || !partnerAvailability ? (
    <div className="flex min-h-[calc(100dvh)] flex-col items-center justify-center">
      <Spinner color="fill-pink-600" />
    </div>
  ) : (
    <MainLayout
      categories={false}
      search={false}
      navbar
      footer
      ssrFetchedData={ssrFetchedData}
    >
      <PartnerSeoPage
        ssrFetchedData={ssrFetchedData}
        partnerProfile={partnerProfile}
        partnerAvailableIntervals={partnerAvailability.intervals}
        selectedBusiness={selectedBusiness}
        selectedService={selectedService}
        selectedInterval={selectedInterval}
        currentCart={selectedCart}
      />
    </MainLayout>
  );
};

export default PartnerProfile;
